import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

function WalletCard({ wallet, showActions = true, className = '' }) {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getOwnerTypeIcon = (ownerType) => {
    switch (ownerType) {
      case 'member':
        return '👤';
      case 'tournament':
        return '🏌️';
      case 'club':
        return '🏢';
      case 'contest':
        return '🏆';
      default:
        return '💰';
    }
  };

  const getOwnerTypeColor = (ownerType) => {
    switch (ownerType) {
      case 'member':
        return 'from-blue-500 to-indigo-600';
      case 'tournament':
        return 'from-purple-500 to-pink-600';
      case 'club':
        return 'from-emerald-500 to-teal-600';
      case 'contest':
        return 'from-amber-500 to-orange-600';
      default:
        return 'from-slate-500 to-slate-600';
    }
  };

  return (
    <Card className={cn("overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-1", className)}>
      <CardContent className="p-0">
        {/* Header with gradient */}
        <div className={cn(
          "bg-gradient-to-r p-4 text-white",
          getOwnerTypeColor(wallet.ownerType)
        )}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="text-3xl">{getOwnerTypeIcon(wallet.ownerType)}</div>
              <div>
                <h3 className="font-semibold text-lg leading-tight">{wallet.ownerName}</h3>
                <span className="text-sm opacity-90">
                  {wallet.ownerType.charAt(0).toUpperCase() + wallet.ownerType.slice(1)} Wallet
                </span>
              </div>
            </div>
            <Badge
              variant={wallet.isActive ? "success" : "secondary"}
              className={cn(
                "text-xs",
                wallet.isActive
                  ? "bg-white/20 text-white border-white/30 hover:bg-white/30"
                  : "bg-black/20 text-white/80 border-white/20"
              )}
            >
              <span className={cn(
                "w-1.5 h-1.5 rounded-full mr-1.5",
                wallet.isActive ? "bg-green-300" : "bg-slate-400"
              )} />
              {wallet.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>

        {/* Balance Section */}
        <div className="p-4 border-b border-slate-100">
          <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Current Balance</div>
          <div className="text-2xl font-bold text-slate-900">{formatCurrency(wallet.balance)}</div>
        </div>

        {/* Last Transaction */}
        {wallet.lastTransaction && (
          <div className="p-4 bg-slate-50 border-b border-slate-100">
            <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">Last Transaction</div>
            <div className="flex items-center justify-between text-sm">
              <Badge variant="outline" className="capitalize">
                {wallet.lastTransaction.type}
              </Badge>
              <span className="font-medium text-slate-700">
                {formatCurrency(wallet.lastTransaction.amount)}
              </span>
              <span className="text-slate-500 text-xs">
                {new Date(wallet.lastTransaction.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div className="p-4 flex flex-wrap gap-2">
            <Button asChild size="sm" className="flex-1">
              <Link to={`/wallets/${wallet._id}`}>View Details</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to={`/wallets/${wallet._id}/deposit`}>Deposit</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to={`/wallets/${wallet._id}/withdraw`}>Withdraw</Link>
            </Button>
          </div>
        )}

        {/* Notes */}
        {wallet.notes && (
          <div className="px-4 pb-4">
            <p className="text-xs text-slate-500 italic">{wallet.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default WalletCard;
